// Parent dialog — stitches the per-section components into a tabbed form
// and wires the Save / Publish / Pause / Test-call actions.
//
// Data model:
//   dealer  : the flat top-level doc (name/type/contact/status)
//   config  : the masked config view from GET /dealers/:id/config
// Saving sends TWO requests — top-level via PATCH /dealers/:id and config
// via PATCH /dealers/:id/config. That mirrors the backend split and lets
// us surface per-layer errors to the admin without conflating them.

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { dealersApi } from '../../../api';

import ProfileSection from './ProfileSection';
import ProductsSection from './ProductsSection';
import ServiceAreaSection from './ServiceAreaSection';
import RoutingSection from './RoutingSection';
import AgentBehaviorSection from './AgentBehaviorSection';
import InventorySection from './InventorySection';
import ComplianceSection from './ComplianceSection';
import IntegrationsSection, { buildIntegrationsPatch } from './IntegrationsSection';
import ReviewSection from './ReviewSection';

const TAB_KEYS = [
  'profile', 'products', 'service_area', 'routing',
  'behavior', 'inventory', 'compliance', 'integrations', 'review',
];

export default function DealerOnboardingDialog({ dealerId, open, onOpenChange, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [dealer, setDealer] = useState(null);
  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState('profile');
  // Twilio token local state — not part of the masked config payload.
  const [tokenState, setTokenState] = useState({ newToken: '', cleared: false });

  useEffect(() => {
    if (!open || !dealerId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setTokenState({ newToken: '', cleared: false });
      try {
        const [d, c] = await Promise.all([
          dealersApi.get(dealerId),
          dealersApi.getConfig(dealerId),
        ]);
        if (!alive) return;
        setDealer(d);
        setConfig(c);
        setTab('profile');
      } catch (err) {
        toast.error(err.message || 'Failed to load dealer');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, dealerId]);

  const setConfigSection = (key) => (v) => setConfig({ ...config, [key]: v });

  const handleSave = async () => {
    if (!dealer || !config) return;
    setSaving(true);
    try {
      await dealersApi.update(dealerId, {
        name: dealer.name,
        status: dealer.status,
        dealer_type: dealer.dealer_type,
        primary_contact_name: dealer.primary_contact_name,
        primary_contact_email: dealer.primary_contact_email,
        primary_phone: dealer.primary_phone,
      });
      const patch = {
        products: config.products,
        service_area: config.service_area,
        call_routing: config.call_routing,
        agent_behavior: config.agent_behavior,
        inventory: config.inventory,
        compliance: config.compliance,
        integrations: buildIntegrationsPatch(config.integrations, tokenState),
      };
      const res = await dealersApi.patchConfig(dealerId, patch);
      // Reset token-state so mask refresh reflects what was saved.
      setTokenState({ newToken: '', cleared: false });
      if (res.config) setConfig(res.config);
      toast.success('Saved');
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setActing(true);
    try {
      await dealersApi.publish(dealerId);
      toast.success('Dealer published');
      if (onChanged) onChanged();
      onOpenChange(false);
    } catch (err) {
      // Validation failures return 422 with structured detail from the backend.
      const detail = err?.data?.detail;
      if (detail?.error === 'validation_failed') {
        toast.error(`Can't publish: ${detail.errors?.[0] || 'missing required fields'}`);
        setTab('review');
      } else {
        toast.error(err.message || 'Publish failed');
      }
    } finally {
      setActing(false);
    }
  };

  const handlePause = async () => {
    setActing(true);
    try {
      await dealersApi.pause(dealerId, 'paused via admin UI');
      toast.success('Dealer paused');
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.message || 'Pause failed');
    } finally {
      setActing(false);
    }
  };

  const handleTestCall = async () => {
    const to = window.prompt('Test-call destination phone number (E.164)');
    if (!to) return;
    setActing(true);
    try {
      const res = await dealersApi.testCall(dealerId, to);
      if (!res.ready) {
        toast.error(`Not ready: ${res.missing?.join('; ') || 'missing Retell config'}`);
      } else if (res.call_result?.success) {
        toast.success('Test call placed');
      } else {
        toast.error(`Retell error: ${res.call_result?.error || 'unknown'}`);
      }
    } catch (err) {
      toast.error(err.message || 'Test call failed');
    } finally {
      setActing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {dealer ? `Configure — ${dealer.name}` : 'Configure dealer'}
          </DialogTitle>
        </DialogHeader>
        {loading || !dealer || !config ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="flex flex-wrap">
              {TAB_KEYS.map((k) => (
                <TabsTrigger key={k} value={k} className="capitalize">{k.replace('_', ' ')}</TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="profile" className="pt-4">
              <ProfileSection dealer={dealer} onChange={setDealer} />
            </TabsContent>
            <TabsContent value="products" className="pt-4">
              <ProductsSection value={config.products} onChange={setConfigSection('products')} />
            </TabsContent>
            <TabsContent value="service_area" className="pt-4">
              <ServiceAreaSection
                value={config.service_area}
                dealerType={dealer.dealer_type}
                onChange={setConfigSection('service_area')}
              />
            </TabsContent>
            <TabsContent value="routing" className="pt-4">
              <RoutingSection value={config.call_routing} onChange={setConfigSection('call_routing')} />
            </TabsContent>
            <TabsContent value="behavior" className="pt-4">
              <AgentBehaviorSection value={config.agent_behavior} onChange={setConfigSection('agent_behavior')} />
            </TabsContent>
            <TabsContent value="inventory" className="pt-4">
              <InventorySection value={config.inventory} onChange={setConfigSection('inventory')} />
            </TabsContent>
            <TabsContent value="compliance" className="pt-4">
              <ComplianceSection value={config.compliance} onChange={setConfigSection('compliance')} />
            </TabsContent>
            <TabsContent value="integrations" className="pt-4">
              <IntegrationsSection
                value={config.integrations}
                onChange={setConfigSection('integrations')}
                tokenState={tokenState}
                setTokenState={setTokenState}
              />
            </TabsContent>
            <TabsContent value="review" className="pt-4 space-y-3">
              <ReviewSection dealerId={dealerId} />
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleTestCall} variant="outline" disabled={acting}>
                  Test call
                </Button>
                <Button onClick={handlePause} variant="outline" disabled={acting}>
                  Pause dealer
                </Button>
                <Button onClick={handlePublish} disabled={acting}>
                  Publish
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving || acting}>Close</Button>
          <Button onClick={handleSave} disabled={loading || saving}>
            {saving ? 'Saving…' : 'Save draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
